<template>
  <div class="navbar-wrapper">
    <!-- Desktop Navigation Drawer -->
    <v-navigation-drawer
      v-if="!isMobile"
      v-model="drawer"
      :mini-variant.sync="mini"
      mini-variant-width="3.5vw"
      :class="['navbar', {'navbar--expanded': !mini}]"
      expand-on-hover
      fixed
      permanent
    >
      <!-- User Profile Section -->
      <v-list-item @click="navigateTo('/profile')" class="profile-item">
        <div class="d-flex align-center mt-3">
          <v-avatar>
            <v-img :src="userImage" alt="User Avatar"></v-img>
          </v-avatar>
          
          <!-- Add Profile text next to avatar with vertical alignment -->
          <span 
            class="ml-2 my-auto text-body-2" 
            style="color:#f8f8f8 !important"
            v-if="!mini"
          >
            {{ $t('profile_Table') }}
          </span>
        </div>
      </v-list-item>

      <!-- Main Navigation -->
      <v-list dense>
        <!-- Analytics Section - Hidden for demo users -->
        <template v-if="!isDemoUser">
          <v-list-item-subtitle style="color:#f8f8f8 !important" class="nav-section-title px-4 py-2">
            {{ $t('sections.analytics') }}
          </v-list-item-subtitle>
          <v-list-item
            v-for="item in analyticsItems"
            :key="item.path"
            :class="['nav-item', { 'nav-item--active': isActive(item.path) }]"
            link
            @click="navigateTo(item.path)"
          >
            <v-list-item-icon class="nav-item__icon">
              <v-icon :color="isActive(item.path) ? 'selectedNav' : 'fff'">
                fa-regular {{ item.icon }}
              </v-icon>
            </v-list-item-icon>

            <v-list-item-content>
              <v-list-item-title :class="{'selected--text': isActive(item.path)}">
                {{ item.title }}
              </v-list-item-title>
            </v-list-item-content>
          </v-list-item>
        </template>

        <!-- Challenges Section -->
        <v-list-item-subtitle style="color:#f8f8f8 !important" class="nav-section-title px-4 py-2">
          {{ $t('sections.challenges') }}
        </v-list-item-subtitle>
        <v-list-item
          v-for="item in challengesItems"
          :key="item.path"
          :class="['nav-item', { 'nav-item--active': isActive(item.path) }]"
          link
          @click="navigateTo(item.path)"
        >
          <v-list-item-icon class="nav-item__icon">
            <v-icon :color="isActive(item.path) ? 'selectedNav' : 'fff'">
              fa-regular {{ item.icon }}
            </v-icon>
          </v-list-item-icon>
          
          <v-list-item-content>
            <v-list-item-title :class="{'selected--text': isActive(item.path)}">
              {{ item.title }}
            </v-list-item-title>
          </v-list-item-content>
        </v-list-item>
      </v-list>

      <!-- Footer Actions -->
      <template v-slot:append>
        <v-list dense>
          <!-- Language Selector -->
          <v-list-item class="nav-item">
            <v-list-item-icon class="nav-item__icon">
              <v-icon color="fff">fa-regular fa-language</v-icon>
            </v-list-item-icon>
            
            <v-list-item-content>
              <LanguageSelector :is-navbar="true" />
            </v-list-item-content>
          </v-list-item>

          <!-- Theme Toggle -->
          <v-list-item
            @click="toggleTheme"
            class="nav-item"
          >
            <v-list-item-icon class="nav-item__icon">
              <v-icon :color="'fff'">
                {{ isDarkTheme ? 'fa-regular fa-sun' : 'fa-regular fa-moon' }}
              </v-icon>
            </v-list-item-icon>
            
            <v-list-item-content>
              <v-list-item-title>
                {{ $t('settheme') }}
              </v-list-item-title>
            </v-list-item-content>
          </v-list-item>

          <!-- Logout -->
          <v-list-item
            @click="handleLogout"
            class="nav-item"
          >
            <v-list-item-icon class="nav-item__icon">
              <v-icon :color="'fff'">
                fa-regular fa-arrow-right-from-bracket
              </v-icon>
            </v-list-item-icon>
            
            <v-list-item-content>
              <v-list-item-title>
                {{ $t('logout') }}
              </v-list-item-title>
            </v-list-item-content>
          </v-list-item>
        </v-list>
      </template>
    </v-navigation-drawer>

    <!-- Mobile Navbar - Fixed at the bottom -->
    <div v-if="isMobile" class="mobile-navbar-container">
      <!-- Hamburger menu button -->
      <button @click="mobileDrawer = !mobileDrawer" class="mobile-navbar-toggle">
        <v-icon color="darkGreen">{{ mobileDrawer ? 'fa-regular fa-xmark' : 'fa-regular fa-bars' }}</v-icon>
      </button>
      
      <!-- Mobile drawer overlay -->
      <div 
        class="mobile-drawer-overlay" 
        :class="{'active': mobileDrawer}"
        @click="mobileDrawer = false"
      ></div>
      
      <!-- Mobile drawer -->
      <div class="mobile-drawer" :class="{'mobile-drawer--open': mobileDrawer}">
        <!-- User profile section -->
        <div class="mobile-user-section" @click="navigateAndClose('/profile')">
          <v-avatar size="60">
            <v-img :src="userImage"></v-img>
          </v-avatar>
          <div class="mobile-user-info">
            <h3>{{ $store.state.user.firstName }} {{ $store.state.user.lastName }}</h3>
            <p>{{ $store.state.user.email }}</p>
          </div>
        </div>
        
        <!-- Analytics Section - Hidden for demo users -->
        <div v-if="!isDemoUser" class="mobile-drawer-section">
          <h4 class="mobile-section-title">{{ $t('sections.analytics') }}</h4>
          <v-list dense nav style="background-color: transparent;">
            <v-list-item
              v-for="item in analyticsItems"
              :key="item.path"
              :class="['mobile-nav-item', { 'mobile-nav-item--active': isActive(item.path) }]"
              @click="navigateAndClose(item.path)"
            >
              <v-list-item-icon>
                <v-icon :color="isActive(item.path) ? 'black' : 'white'">
                  fa-regular {{ item.icon }}
                </v-icon>
              </v-list-item-icon>

              <v-list-item-content>
                <v-list-item-title style="color: white;" :class="{'mobile-selected--text': isActive(item.path)}">
                  {{ item.title }}
                </v-list-item-title>
              </v-list-item-content>
            </v-list-item>
          </v-list>
        </div>
        
        <!-- Challenges Section -->
        <div class="mobile-drawer-section">
          <h4 class="mobile-section-title">{{ $t('sections.challenges') }}</h4>
          <v-list dense nav style="background-color: transparent;">
            <v-list-item
              v-for="item in challengesItems"
              :key="item.path"
              :class="['mobile-nav-item', { 'mobile-nav-item--active': isActive(item.path) }]"
              @click="navigateAndClose(item.path)"
            >
              <v-list-item-icon>
                <v-icon :color="isActive(item.path) ? 'black' : 'white'">
                  fa-regular {{ item.icon }}
                </v-icon>
              </v-list-item-icon>
              
              <v-list-item-content>
                <v-list-item-title :class="{'mobile-selected--text': isActive(item.path)}">
                  {{ item.title }}
                </v-list-item-title>
              </v-list-item-content>
            </v-list-item>
          </v-list>
        </div>
        
        <!-- Mobile Footer Actions -->
        <div class="mobile-drawer-footer">
          <!-- Language Selector -->
          <div class="mobile-language-wrapper">
            <v-icon left class="mr-4">fa-regular fa-language</v-icon>
            <LanguageSelector :is-navbar="true" :is-mobile="true" />
          </div>
          
          <v-btn 
            text 
            block 
            class="mobile-footer-btn" 
            @click="toggleTheme"
          >
            <v-icon left>
              {{ isDarkTheme ? 'fa-regular fa-sun' : 'fa-regular fa-moon' }}
            </v-icon>
            {{ $t('settheme') }}
          </v-btn>
          
          <v-btn 
            text 
            block 
            class="mobile-footer-btn" 
            @click="handleLogout"
          >
            <v-icon left>fa-regular fa-arrow-right-from-bracket</v-icon>
            {{ $t('logout') }}
          </v-btn>
        </div>
      </div>
      
      <!-- Bottom navigation tabs -->
      <div class="mobile-bottom-tabs">
        <div 
          v-for="item in mobileBottomItems" 
          :key="item.path"
          :class="['mobile-tab', {'mobile-tab--active': isActive(item.path)}]"
          @click="navigateTo(item.path)"
        >
          <v-icon :class="{'mobile-tab-icon--active': isActive(item.path)}">
            fa-regular {{ item.icon }}
          </v-icon>
          <span class="mobile-tab-label">{{ item.shortTitle || item.title }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { Auth } from 'aws-amplify';
import { mapState, mapMutations } from 'vuex';
import LanguageSelector from './LanguageSelector.vue';

export default {
  name: 'Navbar',
  
  components: {
    LanguageSelector
  },

  data() {
    return {
      drawer: true,
      mini: true,
      isMobile: false,
      mobileDrawer: false
    };
  },

  computed: {
    ...mapState({
      userImage: state => state.user.imageUrl,
      currentTheme: state => state.currentTheme,
      userId: state => state.user.id,
      userEmail: state => state.user.email
    }),

    isDarkTheme() {
      return this.$vuetify.theme.dark;
    },

    isDemoUser() {
      return this.userId === 596 || this.userEmail === 'demo@swiset.com';
    },

    analyticsItems() {
      // Demo user doesn't see analytics section
      if (this.isDemoUser) {
        return [];
      }
      return [
        {
          title: this.$t('items_Navbar.tableDashboard'),
          shortTitle: this.$t('items_Navbar.shortDashboard'),
          icon: 'fa-tachometer-alt',
          path: '/dashboard'
        },
        {
          title: this.$t('items_Navbar.tableWatchlist'),
          shortTitle: this.$t('items_Navbar.shortWatchlist'),
          icon: 'fa-binoculars',
          path: '/watchlist'
        },
        {
          title: this.$t('items_Navbar.tableJournal'),
          shortTitle: this.$t('items_Navbar.shortJournal'),
          icon: 'fa-stream',
          path: '/journal'
        },
        {
          title: this.$t('items_Navbar.tableMentor'),
          shortTitle: this.$t('items_Navbar.shortMentor'),
          icon: 'fa-brain',
          path: '/mentor',
          beta: true
        },
        {
          title: this.$t('items_Navbar.tablePlan'),
          shortTitle: this.$t('items_Navbar.shortPlan'),
          icon: 'fa-swatchbook',
          path: '/plan'
        }
      ];
    },

    challengesItems() {
      // Demo user only sees Competitions (tournaments) and Community
      if (this.isDemoUser) {
        return [
          {
            title: this.$t('items_Navbar.tableTournaments'),
            shortTitle: this.$t('items_Navbar.shortTournaments'),
            icon: 'fa-trophy',
            path: '/tournaments'
          },
          {
            title: this.$t('items_Navbar.tableCommunity'),
            shortTitle: this.$t('items_Navbar.shortCommunity'),
            icon: 'fa-users',
            path: '/community'
          }
        ];
      }

      // Regular users see all challenges
      return [
        {
          title: this.$t('items_Navbar.tableFunding'),
          shortTitle: this.$t('items_Navbar.shortFunding'),
          icon: 'fa-search-dollar',
          path: '/funding-simulation'
        },
        {
          title: this.$t('items_Navbar.tableTournaments'),
          shortTitle: this.$t('items_Navbar.shortTournaments'),
          icon: 'fa-trophy',
          path: '/tournaments'
        },
        {
          title: this.$t('items_Navbar.tableCommunity'),
          shortTitle: this.$t('items_Navbar.shortCommunity'),
          icon: 'fa-users',
          path: '/community'
        }
      ];
    },
    
    // Items for mobile bottom navigation
    mobileBottomItems() {
      // Demo user only sees Tournaments and Community
      if (this.isDemoUser) {
        return [
          {
            title: this.$t('items_Navbar.tableTournaments'),
            shortTitle: this.$t('items_Navbar.shortTournaments'),
            icon: 'fa-trophy',
            path: '/tournaments'
          },
          {
            title: this.$t('items_Navbar.tableCommunity'),
            shortTitle: this.$t('items_Navbar.shortCommunity'),
            icon: 'fa-users',
            path: '/community'
          }
        ];
      }

      // Regular users see the standard items
      return [
        {
          title: this.$t('items_Navbar.tableDashboard'),
          shortTitle: this.$t('items_Navbar.shortDashboard'),
          icon: 'fa-tachometer-alt',
          path: '/dashboard'
        },
        {
          title: this.$t('items_Navbar.tableJournal'),
          shortTitle: this.$t('items_Navbar.shortJournal'),
          icon: 'fa-stream',
          path: '/journal'
        },
        {
          title: this.$t('items_Navbar.tableTournaments'),
          shortTitle: this.$t('items_Navbar.shortTournaments'),
          icon: 'fa-trophy',
          path: '/tournaments'
        },
        {
          title: this.$t('items_Navbar.tableCommunity'),
          shortTitle: this.$t('items_Navbar.shortCommunity'),
          icon: 'fa-users',
          path: '/community'
        }
      ];
    }
  },

  created() {
    this.checkScreenSize();
    window.addEventListener('resize', this.checkScreenSize);
  },

  beforeDestroy() {
    window.removeEventListener('resize', this.checkScreenSize);
  },

  methods: {
    ...mapMutations(['clearData', 'setCurrentTheme']),

    isActive(path) {
      return this.$route.path.startsWith(path);
    },

    navigateTo(path) {
      if (this.$route.path !== path) {
        this.$router.push(path);
      }
      if (this.isMobile) {
        this.mobileDrawer = false;
      }
    },

    navigateAndClose(path) {
      this.navigateTo(path);
      this.mobileDrawer = false;
    },

    async handleLogout() {
      try {
        await Auth.signOut();
        this.clearData();
        this.$router.push('/');
        this.mobileDrawer = false;
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error during logout:', error);
      }
    },

    toggleTheme() {
      const newTheme = this.isDarkTheme ? 'light' : 'dark';
      this.setCurrentTheme(newTheme);
      this.$vuetify.theme.dark = !this.isDarkTheme;
      localStorage.setItem('theme-preference', newTheme);
    },

    checkScreenSize() {
      this.isMobile = window.innerWidth < 960;
    }
  }
};
</script>



<style lang="scss" scoped>
// Desktop Navbar styles
.navbar {
  background-color: #111111 !important;
  backdrop-filter: none;
  border-radius: 0 5px 5px 0;
  z-index: 1000;

  &--expanded {
    .nav-section-title {
      opacity: 0.8;
    }
  }
}

.nav-item {
  height: 48px;
  display: flex;
  align-items: center;

  &__icon {
    height: 100%;
    display: flex;
    align-items: center;
    margin-top: auto !important;
    margin-bottom: auto !important;
    margin-right: 12px;
    
    i{
      align-items: center;
      height: 100%;
      margin: 0 0px;
      display: flex;
      align-items: center;
      color: #FFF;
      justify-content: center;
      font-size: 16px !important; /* Ensure icons match text size */
    }
  }

  &--active {
    background-color: rgba(255, 255, 255, 0.05);
  }

  .v-list-item__title {
    color: white !important; /* Ensure text is always white */
    font-size: 14px;
    font-weight: 400;
    font-family: 'Avenir', sans-serif;
  }

  .selected--text {
    color: #ffffff !important;
  }
}

// Remove Vuetify's default hover effects
.v-list-item::before {
  display: none;
}

.nav-section-title {
  color: var(--v-mainGreen-base) !important;
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 1px;
  opacity: 0;
  font-weight: 600;
  font-family: 'Avenir', sans-serif;
}

.v-divider {
  border-color: rgba(255, 255, 255, 0.1) !important;
  margin-left: 16px !important;
  margin-right: 16px !important;
}

// Mobile Navbar Styles
.mobile-navbar-container {
  position: relative;
  width: 100%;
  height: 100%;
  z-index: 1000;
}

.mobile-navbar-toggle {
  position: fixed;
  top: 10px;
  right: 10px;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-color: var(--v-mainGreen-base);
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
  z-index: 1002;
  transition: transform 0.3s ease;
  
  &:active {
    transform: scale(0.95);
  }
  
  .v-icon {
    font-size: 18px;
  }
}

// Mobile drawer overlay - darkens the background
.mobile-drawer-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: transparent;
  z-index: 1001;
  visibility: hidden;
  opacity: 0;
  transition: opacity 0.3s ease, visibility 0.3s ease;
  
  &.active {
    visibility: visible;
    opacity: 1;
  }
}

// Mobile drawer slide-in menu
.mobile-drawer {
  position: fixed;
  top: 0;
  right: 0;
  width: 80%;
  max-width: 300px;
  height: 100%;
  background-color: black;
  z-index: 1002;
  transform: translateX(100%);
  transition: transform 0.3s ease;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  
  &--open {
    transform: translateX(0);
    box-shadow: -5px 0 15px rgba(0, 0, 0, 0.2);
  }
  
  // User profile section
  .mobile-user-section {
    padding: 24px 16px;
    background-color: transparent;
    display: flex;
    align-items: center;
    border-bottom: 1px solid rgba(255, 255, 255, 0.06);
    
    .mobile-user-info {
      margin-left: 16px;
      
      h3 {
        color: #f8f8f8 !important;
        font-size: 16px;
        margin: 0;
        font-weight: 500;
      }
      
      p {
        color: #f8f8f8 !important;
        font-size: 12px;
        margin: 4px 0 0;
      }
    }
  }
  
  // Navigation sections
  .mobile-drawer-section {
    padding: 16px 0;
    border-bottom: 1px solid rgba(255, 255, 255, 0.06);
    
    .mobile-section-title {
      color: #f8f8f8 !important;
      font-size: 12px;
      text-transform: uppercase;
      letter-spacing: 1px;
      padding: 0 16px;
      margin: 0 0 8px;
      font-weight: 600;
    }
    
    .mobile-nav-item {
      min-height: 48px;
      
      &--active {
        background-color: var(--v-mainGreen-base);
      }
      
      .v-list-item__title {
        font-size: 15px;
        font-family: "Avenir", sans-serif;
        color: white !important;
      }
      
      .mobile-selected--text {
        color: black !important;
      }
    }
  }
  
  // Footer actions
  .mobile-drawer-footer {
    margin-top: auto;
    padding: 16px;
    
    .mobile-footer-btn {
      color:var(--v-mainWhite-base) !important;
      text-transform: none;
      margin-bottom: 8px;
      height: 44px;
      justify-content: flex-start;
      
      &:last-child {
        margin-bottom: 0;
      }
      
      .v-icon {
        margin-right: 16px;
      }
    }
  }
  
  .mobile-language-wrapper {
    display: flex;
    align-items: center;
    color: white;
    padding: 8px 16px;
    margin-bottom: 16px;
  }
}

// Bottom navigation tabs (mobile only)
.mobile-bottom-tabs {
  position: fixed;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 60px;
  background-color: var(--v-mainWhite-base);
  display: flex;
  justify-content: space-around;
  z-index: 1000;
  box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.1);
  
  .mobile-tab {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    color: rgba(255, 255, 255, 0.6);
    transition: color 0.2s ease;
    position: relative;
    
    &--active {
      color: var(--v-mainGreen-base);
      
      &::after {
        content: '';
        position: absolute;
        top: 0;
        left: 50%;
        width: 30px;
        height: 3px;
        background-color: var(--v-mainGreen-base);
        transform: translateX(-50%);
        border-radius: 0 0 3px 3px;
      }
    }
    
    .v-icon {
      font-size: 20px;
      margin-bottom: 4px;
    }
    
    .mobile-tab-icon--active {
      color: var(--v-mainGreen-base);
    }
    
    .mobile-tab-label {
      font-size: 10px;
      font-weight: 500;
    }
  }
}

// Adding a simple style for the wrapper element
.navbar-wrapper {
  height: 100%;
  position: relative;
}

.language-selector-nav {
  display: none;
}
</style>
